/**
 * Date utility functions for consistent date formatting
 * Supports project-specific timezones with abbreviations (e.g., "Dec 15, 2025 CST")
 */

// Timezone abbreviation lookup
const TIMEZONE_ABBREVS = {
  'America/New_York': { standard: 'EST', daylight: 'EDT' },
  'America/Chicago': { standard: 'CST', daylight: 'CDT' },
  'America/Denver': { standard: 'MST', daylight: 'MDT' },
  'America/Phoenix': { standard: 'MST', daylight: 'MST' }, // Arizona doesn't observe DST
  'America/Los_Angeles': { standard: 'PST', daylight: 'PDT' },
  'America/Anchorage': { standard: 'AKST', daylight: 'AKDT' },
  'Pacific/Honolulu': { standard: 'HST', daylight: 'HST' }, // Hawaii doesn't observe DST
  'America/Detroit': { standard: 'EST', daylight: 'EDT' },
  'America/Indiana/Indianapolis': { standard: 'EST', daylight: 'EDT' },
  'America/Kentucky/Louisville': { standard: 'EST', daylight: 'EDT' },
  'America/Boise': { standard: 'MST', daylight: 'MDT' },
};

/**
 * Get timezone abbreviation for a given IANA timezone name
 * @param {string} timezone - IANA timezone name (e.g., "America/Chicago")
 * @param {Date} date - Date to check for DST (optional, defaults to now)
 * @returns {string} Timezone abbreviation (e.g., "CST" or "CDT")
 */
export const getTimezoneAbbrev = (timezone, date = new Date()) => {
  if (!timezone) return '';
  
  const tzInfo = TIMEZONE_ABBREVS[timezone];
  if (!tzInfo) {
    // Fallback: extract from timezone name
    const parts = timezone.split('/');
    return parts[parts.length - 1].substring(0, 4).toUpperCase();
  }
  
  // Check if DST is in effect
  try {
    const options = { timeZone: timezone, timeZoneName: 'short' };
    const formatted = date.toLocaleString('en-US', options);
    // Check if the formatted string contains daylight indicators
    const isDST = formatted.includes('DT') || formatted.includes('Daylight');
    return isDST ? tzInfo.daylight : tzInfo.standard;
  } catch {
    return tzInfo.standard;
  }
};

/**
 * Format a date string or Date object to MM/DD/YYYY
 * DrawReady's canonical user-facing date format.
 * @param {string|Date} dateInput - ISO date string, Date object, or YYYY-MM-DD string
 * @returns {string} Formatted date string in MM/DD/YYYY format
 */
export const formatDate = (dateInput) => {
  if (!dateInput) return '';
  
  // If it's a string in YYYY-MM-DD format, parse it directly to avoid timezone issues
  if (typeof dateInput === 'string') {
    const dateOnly = dateInput.split('T')[0];
    const parts = dateOnly.split('-');
    if (parts.length === 3) {
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10);
      const day = parseInt(parts[2], 10);
      // Pad month and day with leading zeros
      const monthStr = month.toString().padStart(2, '0');
      const dayStr = day.toString().padStart(2, '0');
      return `${monthStr}/${dayStr}/${year}`;
    }
  }
  
  // For Date objects or other formats, format manually
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (isNaN(date.getTime())) return '';
  
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

/**
 * Format a date with month name (e.g., "Jan 15, 2024")
 * @param {string|Date} dateInput - Date input
 * @returns {string} Formatted date string
 */
export const formatDateWithMonth = (dateInput) => {
  if (!dateInput) return '';
  
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  // Parse YYYY-MM-DD strings directly
  if (typeof dateInput === 'string') {
    const dateOnly = dateInput.split('T')[0];
    const parts = dateOnly.split('-');
    if (parts.length === 3) {
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const day = parseInt(parts[2], 10);
      return `${months[month]} ${day}, ${year}`;
    }
  }
  
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (isNaN(date.getTime())) return '';
  
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
};

/**
 * Format a date with month name and timezone abbreviation (e.g., "Dec 15, 2025 CST")
 * This is the primary format for displaying dates with project timezone context
 * @param {string|Date} dateInput - Date input
 * @param {string} timezone - IANA timezone name (e.g., "America/Chicago")
 * @returns {string} Formatted date string with timezone abbreviation
 */
export const formatDateWithTimezone = (dateInput, timezone) => {
  if (!dateInput) return '';
  
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  // Parse YYYY-MM-DD strings directly to avoid timezone shifting
  if (typeof dateInput === 'string') {
    const dateOnly = dateInput.split('T')[0];
    const parts = dateOnly.split('-');
    if (parts.length === 3) {
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const day = parseInt(parts[2], 10);
      const dateStr = `${months[month]} ${day}, ${year}`;
      
      // Add timezone abbreviation if provided
      if (timezone) {
        const abbrev = getTimezoneAbbrev(timezone, new Date(year, month, day));
        return `${dateStr} ${abbrev}`;
      }
      return dateStr;
    }
  }
  
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (isNaN(date.getTime())) return '';
  
  const dateStr = `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  
  if (timezone) {
    const abbrev = getTimezoneAbbrev(timezone, date);
    return `${dateStr} ${abbrev}`;
  }
  
  return dateStr;
};

/**
 * Format a date with short month (e.g., "Jan 15")
 * @param {string|Date} dateInput - Date input
 * @returns {string} Formatted date string
 */
export const formatDateShort = (dateInput) => {
  if (!dateInput) return '';
  
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  if (typeof dateInput === 'string') {
    const dateOnly = dateInput.split('T')[0];
    const parts = dateOnly.split('-');
    if (parts.length === 3) {
      const month = parseInt(parts[1], 10) - 1;
      const day = parseInt(parts[2], 10);
      return `${months[month]} ${day}`;
    }
  }
  
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (isNaN(date.getTime())) return '';
  
  return `${months[date.getMonth()]} ${date.getDate()}`;
};

/**
 * Format a date with short month and timezone (e.g., "Jan 15 CST")
 * @param {string|Date} dateInput - Date input
 * @param {string} timezone - IANA timezone name
 * @returns {string} Formatted date string with timezone abbreviation
 */
export const formatDateShortWithTimezone = (dateInput, timezone) => {
  if (!dateInput) return '';
  
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  if (typeof dateInput === 'string') {
    const dateOnly = dateInput.split('T')[0];
    const parts = dateOnly.split('-');
    if (parts.length === 3) {
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const day = parseInt(parts[2], 10);
      const dateStr = `${months[month]} ${day}`;
      
      if (timezone) {
        const abbrev = getTimezoneAbbrev(timezone, new Date(year, month, day));
        return `${dateStr} ${abbrev}`;
      }
      return dateStr;
    }
  }
  
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (isNaN(date.getTime())) return '';
  
  const dateStr = `${months[date.getMonth()]} ${date.getDate()}`;
  
  if (timezone) {
    const abbrev = getTimezoneAbbrev(timezone, date);
    return `${dateStr} ${abbrev}`;
  }
  
  return dateStr;
};

/**
 * Format current date/time for display
 * @returns {string} Current date in MM/DD/YYYY format
 */
export const formatCurrentDate = () => {
  const now = new Date();
  const month = (now.getMonth() + 1).toString().padStart(2, '0');
  const day = now.getDate().toString().padStart(2, '0');
  const year = now.getFullYear();
  return `${month}/${day}/${year}`;
};

/**
 * Get today's date in YYYY-MM-DD format for input fields
 * @returns {string} Today's date in YYYY-MM-DD format
 */
export const getTodayISO = () => {
  const now = new Date();
  return now.toISOString().split('T')[0];
};

/**
 * Parse a date string safely, avoiding timezone issues
 * @param {string} dateStr - Date string in YYYY-MM-DD or ISO format
 * @returns {Date} Date object
 */
export const parseDate = (dateStr) => {
  if (!dateStr) return null;
  
  const dateOnly = dateStr.split('T')[0];
  const parts = dateOnly.split('-');
  
  if (parts.length === 3) {
    return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
  }
  
  return new Date(dateStr);
};

export default {
  formatDate,
  formatDateWithMonth,
  formatDateWithTimezone,
  formatDateShort,
  formatDateShortWithTimezone,
  formatCurrentDate,
  getTodayISO,
  parseDate,
  getTimezoneAbbrev
};
